import { useState } from "react";

import { IAdminPatientDevice } from "../../../../@types/admin/AdminDevice";
import { useUpdateDeviceIdentifier } from "../../../../hooks/mutations/admin/devices";
import { useToast } from "../../../../hooks/useToast";
import { errorToastMessage } from "../../../../utils/HttpError";
import {
  FieldError,
  FieldHint,
  FilterButton,
  FilterGroup,
  FilterInput,
  FilterLabel,
  ModalContainer,
  ModalForm,
  ModalFormActions,
  ModalTitle,
} from "../AdminPage.styles";
import { validateDeviceIdentifier } from "./deviceIdentifier";

interface IEditIdentifierModalProps {
  device: IAdminPatientDevice;
  onClose: () => void;
}

/** Renames a device's identifier — Retool's `modalEditIdentifier`. */
export function EditIdentifierModal({
  device,
  onClose,
}: IEditIdentifierModalProps) {
  const { showToast } = useToast();
  const { updateIdentifier, isUpdatingIdentifier } =
    useUpdateDeviceIdentifier();

  const [value, setValue] = useState(device.identifier);

  const error = validateDeviceIdentifier(value);
  const isUnchanged = value === device.identifier;

  const handleSubmit = async () => {
    if (error || isUnchanged) return;

    try {
      await updateIdentifier({
        identifier: device.identifier,
        newIdentifier: value.trim(),
      });
      showToast("The device identifier was updated.", "success");
      onClose();
    } catch (error) {
      showToast(
        errorToastMessage(error, "The device identifier could not be updated."),
        "warning"
      );
    }
  };

  return (
    <ModalContainer>
      <ModalTitle>Edit identifier</ModalTitle>

      <ModalForm>
        <FilterGroup>
          <FilterLabel htmlFor="device-identifier">Identifier</FilterLabel>
          <FilterInput
            id="device-identifier"
            type="text"
            value={value}
            disabled={isUpdatingIdentifier}
            onChange={(e) => setValue(e.target.value)}
          />
          <FieldHint>
            Exactly 15 digits. Readings already recorded against{" "}
            {device.identifier} stay with this device.
          </FieldHint>
          {error && <FieldError>{error}</FieldError>}
        </FilterGroup>

        <ModalFormActions>
          <FilterButton
            variant="secondary"
            disabled={isUpdatingIdentifier}
            onClick={onClose}
          >
            Cancel
          </FilterButton>
          <FilterButton
            variant="primary"
            disabled={isUpdatingIdentifier || Boolean(error) || isUnchanged}
            onClick={handleSubmit}
          >
            {isUpdatingIdentifier ? "Updating..." : "Update"}
          </FilterButton>
        </ModalFormActions>
      </ModalForm>
    </ModalContainer>
  );
}
